import React, { useMemo } from 'react';
import { Search, Plus, Pin, Archive, MoreVertical, Wifi, WifiOff, MessageSquare, Loader2, Filter } from 'lucide-react';
import { avatarColor, initials, relativeTime, readLocal, writeLocal, PIN_KEY, ARCHIVE_KEY } from './utils';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'archived', label: 'Archived' },
];

const ChatRow = ({ chat, active, pinned, onSelect, onPin, onArchive, archived }) => {
  const stopClick = (e) => e.stopPropagation();
  return (
    <div
      onClick={() => onSelect(chat)}
      className={`group relative flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-gray-100/70 transition-colors
        ${active ? 'bg-teal-50/80' : 'hover:bg-gray-50'}`}
      data-testid={`inbox-chat-row-${chat.phone}`}
    >
      {/* Avatar */}
      <div className={`h-12 w-12 shrink-0 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-inner ${avatarColor(chat.phone)}`}>
        {initials(chat.recipientName, chat.phone)}
      </div>

      {/* Middle */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className={`truncate text-sm ${chat.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>
            {chat.recipientName || chat.phone}
          </h4>
          {pinned && <Pin size={11} className="text-gray-400 shrink-0 rotate-45" />}
          <span className="ml-auto text-[11px] text-gray-400 shrink-0 tabular-nums">
            {relativeTime(chat.lastMessageAt)}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <p className={`truncate text-xs flex-1 ${chat.unreadCount > 0 ? 'text-gray-700' : 'text-gray-500'}`}>
            {chat.lastDirection === 'sent' && <span className="text-gray-400">✓ </span>}
            {chat.lastMessage || 'No messages yet'}
          </p>
          {chat.unreadCount > 0 && (
            <span className="bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center shrink-0"
              data-testid={`inbox-unread-${chat.phone}`}>
              {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
            </span>
          )}
        </div>
      </div>

      {/* Hover actions */}
      <div
        onClick={stopClick}
        className="absolute right-2 top-2 hidden group-hover:flex items-center gap-1 bg-white/90 backdrop-blur rounded-md border border-gray-200 px-1 py-0.5 shadow-sm"
      >
        <button
          onClick={() => onPin(chat.phone)}
          className={`p-1 rounded hover:bg-gray-100 ${pinned ? 'text-teal-600' : 'text-gray-500'}`}
          title={pinned ? 'Unpin' : 'Pin'}
          data-testid={`inbox-pin-${chat.phone}`}
        >
          <Pin size={13} className={pinned ? 'rotate-45' : ''} />
        </button>
        <button
          onClick={() => onArchive(chat.phone)}
          className={`p-1 rounded hover:bg-gray-100 ${archived ? 'text-amber-600' : 'text-gray-500'}`}
          title={archived ? 'Unarchive' : 'Archive'}
          data-testid={`inbox-archive-${chat.phone}`}
        >
          <Archive size={13} />
        </button>
      </div>
    </div>
  );
};

const ChatSidebar = ({
  chats, loading, selectedPhone, onSelect,
  search, setSearch, filter, setFilter,
  wsConnected, onNewChat, onRefresh,
  pinned, archived, setPinned, setArchived,
}) => {
  const togglePin = (phone) => {
    const next = pinned.includes(phone) ? pinned.filter(p => p !== phone) : [...pinned, phone];
    setPinned(next); writeLocal(PIN_KEY, next);
  };
  const toggleArchive = (phone) => {
    const next = archived.includes(phone) ? archived.filter(p => p !== phone) : [...archived, phone];
    setArchived(next); writeLocal(ARCHIVE_KEY, next);
  };

  const { visibleChats, counts } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchesSearch = (c) => !q ||
      c.phone?.toLowerCase().includes(q) ||
      (c.recipientName || '').toLowerCase().includes(q) ||
      (c.lastMessage || '').toLowerCase().includes(q);

    const isArchived = (c) => archived.includes(c.phone);
    const isAssigned = (c) => !!(c.leadId || c.propertyId);
    const isUnread = (c) => (c.unreadCount || 0) > 0;

    const base = chats.filter(matchesSearch);

    const c = {
      all: base.filter(x => !isArchived(x)).length,
      unread: base.filter(x => !isArchived(x) && isUnread(x)).length,
      assigned: base.filter(x => !isArchived(x) && isAssigned(x)).length,
      archived: base.filter(isArchived).length,
    };

    let list = base;
    if (filter === 'archived') list = base.filter(isArchived);
    else if (filter === 'unread') list = base.filter(x => !isArchived(x) && isUnread(x));
    else if (filter === 'assigned') list = base.filter(x => !isArchived(x) && isAssigned(x));
    else list = base.filter(x => !isArchived(x));

    // Sort: pinned first → unknown chats next (mirror backend pin) → then by recency.
    // The "isUnknown" tier MUST stay near the top so newly-arrived messages
    // from numbers without a Lead/Property linkage never get buried under
    // older assigned threads.
    const isUnk = (c) => !!c.isUnknown || (!c.leadId && !c.propertyId);
    list = [...list].sort((a, b) => {
      const ap = pinned.includes(a.phone) ? 1 : 0;
      const bp = pinned.includes(b.phone) ? 1 : 0;
      if (ap !== bp) return bp - ap;
      const ua = isUnk(a) ? 1 : 0;
      const ub = isUnk(b) ? 1 : 0;
      if (ua !== ub) return ub - ua;
      return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
    });

    return { visibleChats: list, counts: c };
  }, [chats, search, filter, pinned, archived]);

  return (
    <aside className="flex flex-col w-full md:w-[340px] lg:w-[380px] shrink-0 bg-white border-r border-gray-200"
      data-testid="inbox-sidebar">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
              <MessageSquare size={16} className="text-white" />
            </div>
            <h3 className="font-bold text-gray-900 tracking-tight">Inbox</h3>
            {wsConnected
              ? <Wifi size={14} className="text-emerald-500" title="Live" />
              : <WifiOff size={14} className="text-gray-300" title="Offline" />}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onRefresh} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500" title="Refresh" data-testid="inbox-refresh-btn">
              <Filter size={16} className="rotate-180" />
            </button>
            <button onClick={onNewChat} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500" title="New chat" data-testid="inbox-new-chat-btn">
              <Plus size={18} />
            </button>
            <button className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500" title="More">
              <MoreVertical size={16} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search or start new chat"
            className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500/40 focus:bg-white transition-all"
            data-testid="inbox-search-input"
          />
        </div>

        {/* Filter Pills */}
        <div className="flex gap-1.5 mt-3 overflow-x-auto scrollbar-none -mx-1 px-1">
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded-full transition-colors border
                ${filter === f.id
                  ? 'bg-emerald-500 text-white border-emerald-500 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-600'}`}
              data-testid={`inbox-filter-${f.id}`}
            >
              {f.label}
              {counts[f.id] > 0 && (
                <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${filter === f.id ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`}>
                  {counts[f.id]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-emerald-500" size={22} />
          </div>
        ) : visibleChats.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-12 px-6">
            <MessageSquare size={28} className="mx-auto mb-2 text-gray-300" />
            {search ? 'No chats match your search' : filter === 'archived' ? 'No archived chats' : 'No conversations yet'}
          </div>
        ) : (
          visibleChats.map(chat => (
            <ChatRow
              key={chat.phone}
              chat={chat}
              active={selectedPhone === chat.phone}
              pinned={pinned.includes(chat.phone)}
              archived={archived.includes(chat.phone)}
              onSelect={onSelect}
              onPin={togglePin}
              onArchive={toggleArchive}
            />
          ))
        )}
      </div>
    </aside>
  );
};

export default ChatSidebar;
